import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import api from '../../utils/api';
import EntHero from '../../components/enterprise/EntHero';
import EntStatCard from '../../components/enterprise/EntStatCard';
import EntSkeleton from '../../components/enterprise/EntSkeleton';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const CHART_COLORS = { gold: '#C6A75E', goldLight: 'rgba(198, 167, 94, 0.4)', panel: '#2A2A2E' };
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: CHART_COLORS.panel,
      titleColor: '#E6E6E8',
      bodyColor: '#E6E6E8',
    },
  },
  scales: {
    x: { grid: { color: 'rgba(230,230,232,0.08)' }, ticks: { color: 'rgba(230,230,232,0.72)' } },
    y: { grid: { color: 'rgba(230,230,232,0.08)' }, ticks: { color: 'rgba(230,230,232,0.72)' } },
  },
};

export default function EnterpriseAnalyticsPage() {
  const { enterpriseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId');
  const [members, setMembers] = useState([]);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enterpriseId) return;
    let cancelled = false;
    api.get('/api/enterprise/members', { params: { enterpriseId } })
      .then((res) => { if (!cancelled) setMembers(res.data.members || []); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || 'Failed to load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enterpriseId]);

  useEffect(() => {
    if (!enterpriseId || !userId) {
      setUserAnalytics(null);
      return;
    }
    let cancelled = false;
    setUserAnalytics(null);
    api.get(`/api/enterprise/user/${userId}/analytics`, { params: { enterpriseId } })
      .then((res) => { if (!cancelled) setUserAnalytics(res.data); })
      .catch(() => { if (!cancelled) setUserAnalytics(null); });
    return () => { cancelled = true; };
  }, [enterpriseId, userId]);

  const leaderboardData = useMemo(() => {
    if (!members.length) return [];
    return members.map((m) => ({ id: m.userId, name: m.name, email: m.email }));
  }, [members]);

  const lineChartData = useMemo(() => {
    if (!userAnalytics?.contacts_over_time?.length) return null;
    const d = userAnalytics.contacts_over_time;
    return {
      labels: d.map((x) => x.label),
      datasets: [{
        label: 'Contacts added',
        data: d.map((x) => x.count),
        borderColor: CHART_COLORS.gold,
        backgroundColor: CHART_COLORS.goldLight,
        fill: true,
        tension: 0.3,
      }],
    };
  }, [userAnalytics?.contacts_over_time]);

  const barChartData = useMemo(() => {
    if (!userAnalytics?.weekly_engagement?.length) return null;
    const d = userAnalytics.weekly_engagement;
    return {
      labels: d.map((x) => x.day),
      datasets: [{
        label: 'Activity',
        data: d.map((x) => x.count),
        backgroundColor: CHART_COLORS.goldLight,
        borderColor: CHART_COLORS.gold,
        borderWidth: 1,
      }],
    };
  }, [userAnalytics?.weekly_engagement]);

  const hero = (
    <EntHero
      eyebrow="Performance"
      title="Analytics"
      lead="Per-employee networking metrics. Select a member to view their contacts, engagement, and activity trends."
    />
  );

  if (loading) {
    return (
      <div className="ent-content">
        {hero}
        <EntSkeleton variant="rows" count={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="ent-content">
        {hero}
        <div className="ent-flash ent-flash-error" role="alert">{error}</div>
      </div>
    );
  }

  const selectedMember = members.find((m) => m.userId === userId);

  return (
    <div className="ent-content">
      {hero}

      {userId && userAnalytics && (
        <>
          <section className="ent-section">
            <h2 className="ent-section-title">Employee: {selectedMember?.name || selectedMember?.email || userId}</h2>
            <div className="ent-cards" style={{ marginBottom: 24 }}>
              <EntStatCard label="Contacts added" value={userAnalytics.contacts_added ?? 0} />
              <EntStatCard label="Cards shared" value={userAnalytics.card_taps ?? 0} />
              <EntStatCard label="Meeting invites" value={userAnalytics.meeting_invites ?? 0} />
              <EntStatCard label="Active days (30d)" value={userAnalytics.active_days ?? 0} />
              <EntStatCard label="Best time" value={userAnalytics.best_time || '—'} valueClassName="ent-card-value-sm" />
            </div>
            {lineChartData && (
              <div className="ent-chart-container">
                <Line data={lineChartData} options={chartOptions} />
              </div>
            )}
            {barChartData && (
              <div className="ent-chart-container">
                <Bar data={barChartData} options={chartOptions} />
              </div>
            )}
            {userAnalytics.activity_by_hour && (
              <div className="ent-section">
                <h3 className="ent-section-title">Activity by hour (heatmap data)</h3>
                <div className="ent-table-wrap">
                  <table className="ent-table">
                    <thead>
                      <tr>
                        <th>Hour</th>
                        <th>Activity count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userAnalytics.activity_by_hour.map((count, hour) => (
                        <tr key={hour}>
                          <td>{String(hour).padStart(2, '0')}:00</td>
                          <td>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <section className="ent-section">
        <h2 className="ent-section-title">Members — view analytics</h2>
        <div className="ent-table-wrap">
          <table className="ent-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {leaderboardData.length === 0 ? (
                <tr><td colSpan={3} className="ent-empty">No members.</td></tr>
              ) : (
                leaderboardData.map((m) => (
                  <tr key={m.id}>
                    <td>{m.name}</td>
                    <td>{m.email}</td>
                    <td>
                      <button
                        type="button"
                        className="ent-btn-primary"
                        style={{ padding: '6px 12px', fontSize: '0.875rem' }}
                        onClick={() => setSearchParams({ userId: m.id })}
                      >
                        View analytics
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
